import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../core/AppContext';

// Standalone LLM Council validation tab. Validates prompt/text content against criteria
// with the 3-judge panel + chief judge, showing each judge's steps and the final verdict.
// Same backend council as Global Model Eval, exposed via /api/council/*.
const KIND = {
  received: { icon: '📥', color: '#4a5568' }, thought: { icon: '💭', color: '#6f42c1' },
  action: { icon: '🛠️', color: '#0073bb' }, observation: { icon: '👁️', color: '#2f855a' },
  a2a_send: { icon: '📡', color: '#b7791f' }, result: { icon: '✅', color: '#155724' },
  info: { icon: 'ℹ️', color: '#4a5568' }, error: { icon: '⚠️', color: '#b00020' }, done: { icon: '🏁', color: '#155724' },
};
const API = '/api';

export default function CouncilView() {
  const { can, getAuthHeaders } = useApp();
  const allowed = can('use_council') || can('manage_config');
  const [type, setType] = useState('prompt');
  const [mode, setMode] = useState('score');
  const [content, setContent] = useState('');
  const [criteria, setCriteria] = useState('');
  const [cfg, setCfg] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState('');
  const sinceRef = useRef(0);
  const headersRef = useRef(getAuthHeaders); headersRef.current = getAuthHeaders;

  useEffect(() => {
    if (!allowed) return;
    fetch(`${API}/council/config`, { headers: getAuthHeaders() }).then(r => r.json()).then(setCfg).catch(() => {});
  // eslint-disable-next-line
  }, [allowed]);

  useEffect(() => {
    if (!jobId) return undefined;
    let stop = false;
    sinceRef.current = 0; setEvents([]); setJob(null); setResult(null);
    const tick = async () => {
      if (stop) return;
      try {
        const r = await fetch(`${API}/council/validate/${jobId}?since=${sinceRef.current}`, { headers: headersRef.current() });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setMsg(d.error || `HTTP ${r.status}`); return; }
        if (d.events && d.events.length) setEvents(prev => prev.concat(d.events));
        sinceRef.current = d.next || sinceRef.current;
        setJob(d);
        if (d.status !== 'RUNNING') {
          if (d.status === 'DONE' && d.result) { setResult(d.result); setHistory(h => [{ at: new Date(), type, mode, result: d.result }, ...h].slice(0, 10)); }
          if (d.status === 'FAILED') setMsg(`Validation failed: ${d.error || 'unknown'}`);
          return;
        }
      } catch (e) { setMsg('Lost connection while polling.'); }
      if (!stop) setTimeout(tick, 1000);
    };
    tick();
    return () => { stop = true; };
  // eslint-disable-next-line
  }, [jobId]);

  const run = async () => {
    setMsg('');
    if (!content.trim()) { setMsg('Enter the content to validate.'); return; }
    try {
      const r = await fetch(`${API}/council/validate`, { method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ type, mode, content, criteria }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.job_id) throw new Error(d.error || `HTTP ${r.status}`);
      setJobId(d.job_id);
    } catch (e) { setMsg(`Could not start: ${e.message}`); }
  };

  if (!allowed) return <div style={{ padding: 20, color: '#721c24', background: '#f8d7da', borderRadius: 8 }}>You need the <code>use_council</code> privilege. Ask an admin.</div>;

  const agents = ['coordinator', ...Array.from(new Set(events.map(e => e.agent).filter(a => a !== 'coordinator' && a !== 'chief'))), ...(events.some(e => e.agent === 'chief') ? ['chief'] : [])];
  const running = job && job.status === 'RUNNING';
  const inp = { width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', fontSize: 13 };
  const verdictText = result && (result.mode === 'safety' ? (result.safe ? '✅ SAFE' : '⛔ UNSAFE')
    : result.mode === 'pass_fail' ? (result.pass ? `✅ PASS (${result.score})` : `⛔ FAIL (${result.score})`)
    : `Score ${result.score}`);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ ...{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }, display: 'flex', alignItems: 'center', gap: 15, marginBottom: 15 }}>
        <div style={{ fontSize: 34 }}>🧑‍⚖️</div>
        <div>
          <h2 style={{ margin: 0, color: '#0f2027' }}>LLM Council Validation</h2>
          <p style={{ margin: '4px 0 0', color: '#718096', fontSize: 13 }}>
            Validate a prompt or text with a {cfg ? cfg.judges.length : 3}-judge council{cfg && cfg.chief_judge ? ' + chief judge' : ''}
            {cfg ? ` · ${cfg.mode === 'a2a' ? 'A2A agents' : 'direct'} judges` : ''}. Also available as a REST API and an A2A agent for other agents.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 15, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 360px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <label style={{ flex: 1, fontSize: 12 }}>Type
              <select value={type} onChange={e => setType(e.target.value)} style={inp}>
                <option value="prompt">Prompt</option><option value="text">Text</option>
                <option value="image" disabled>Image (coming soon)</option>
                <option value="video" disabled>Video (coming soon)</option>
              </select>
            </label>
            <label style={{ flex: 1, fontSize: 12 }}>Mode
              <select value={mode} onChange={e => setMode(e.target.value)} style={inp}>
                <option value="score">Score (0-100)</option><option value="pass_fail">Pass / Fail</option><option value="safety">Safety</option>
              </select>
            </label>
          </div>
          <label style={{ fontSize: 12 }}>Content to validate
            <textarea rows={6} value={content} onChange={e => setContent(e.target.value)} placeholder="Paste the prompt or text to validate…" style={{ ...inp, resize: 'vertical' }} />
          </label>
          <label style={{ fontSize: 12, display: 'block', marginTop: 8 }}>Criteria {mode !== 'safety' && <span style={{ color: '#a0aec0' }}>(what makes this good?)</span>}
            <textarea rows={3} value={criteria} onChange={e => setCriteria(e.target.value)} placeholder={mode === 'safety' ? 'Optional: safety policy to apply' : 'e.g. on-brand, accurate, no unverifiable claims'} style={{ ...inp, resize: 'vertical' }} />
          </label>
          <button onClick={run} disabled={running} style={{ marginTop: 10, padding: '9px 16px', background: running ? '#90cdf4' : '#0073bb', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 'bold', cursor: running ? 'default' : 'pointer' }}>
            {running ? 'Validating…' : 'Validate with council'}
          </button>
          {msg && <p style={{ color: '#b00020', fontSize: 12 }}>{msg}</p>}
          {result && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 6, border: `2px solid ${(result.safe === false || result.pass === false) ? '#dc3545' : '#28a745'}`, background: (result.safe === false || result.pass === false) ? '#fff5f5' : '#f0fff4' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>{verdictText}</div>
              {result.by_judge && <div style={{ fontSize: 12, color: '#4a5568', marginTop: 4 }}>Panel: {Object.entries(result.by_judge).map(([k, v]) => `${k} ${v}`).join(' · ')}{result.panel_median != null && result.chief_used ? ` → chief ${result.score}` : ''}</div>}
              {result.chief_used && <div style={{ fontSize: 11, color: '#6f42c1', marginTop: 2 }}>Chief judge decided the final score. {result.chief_reason || ''}</div>}
            </div>
          )}
          {history.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', color: '#718096' }}>Recent</div>
              {history.map((h, i) => (
                <div key={i} style={{ fontSize: 11, color: '#555', borderTop: '1px solid #edf2f7', padding: '3px 0' }}>
                  {h.type}/{h.mode}: {h.result.mode === 'safety' ? (h.result.safe ? 'SAFE' : 'UNSAFE') : h.result.mode === 'pass_fail' ? (h.result.pass ? 'PASS' : 'FAIL') : `score ${h.result.score}`} · {h.at.toLocaleTimeString()}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '2 1 520px', background: '#fbfcfe', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, minWidth: 320 }}>
          <div style={{ fontSize: 13, fontWeight: 'bold', color: '#0f2027', marginBottom: 8 }}>
            Live council trace {job ? <span style={{ fontWeight: 'normal', color: '#718096', fontSize: 11 }}>· {job.status}{events.length ? ` · ${events.length} steps` : ''}</span> : <span style={{ color: '#a0aec0', fontWeight: 'normal', fontSize: 11 }}>· idle</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
            {agents.length === 0 && <div style={{ fontSize: 12, color: '#a0aec0' }}>Run a validation to see the judges work.</div>}
            {agents.map(a => (
              <div key={a} style={{ flex: '1 0 240px', minWidth: 240, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <div style={{ padding: '6px 8px', borderBottom: '1px solid #edf2f7', fontSize: 12, fontWeight: 'bold',
                  background: a === 'coordinator' ? '#eef6fc' : a === 'chief' ? '#fff8e6' : '#f7f3ff',
                  color: a === 'coordinator' ? '#0073bb' : a === 'chief' ? '#b7791f' : '#6f42c1' }}>
                  {a === 'coordinator' ? '🧭 Coordinator' : a === 'chief' ? '⚖️ Chief judge' : `🤖 ${a}`}
                </div>
                <div style={{ maxHeight: 420, overflowY: 'auto', padding: '6px 8px' }}>
                  {events.filter(e => e.agent === a).map((e, i) => {
                    const k = KIND[e.kind] || KIND.info;
                    return (
                      <div key={i} style={{ fontSize: 11, marginBottom: 6, borderLeft: `3px solid ${k.color}`, paddingLeft: 6 }}>
                        <div style={{ color: k.color, fontWeight: 'bold' }}>{k.icon} {e.kind} <span style={{ color: '#a0aec0', fontWeight: 'normal' }}>{e.t}s</span></div>
                        <div style={{ color: '#2d3748', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.text}</div>
                      </div>
                    );
                  })}
                  {events.filter(e => e.agent === a).length === 0 && <div style={{ fontSize: 11, color: '#a0aec0' }}>{running ? 'waiting…' : ''}</div>}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10, color: '#a0aec0', marginTop: 6 }}>"Thinking" is each judge's visible reasoning and tool calls — not hidden chain-of-thought.</div>
        </div>
      </div>
    </div>
  );
}
